<template>
  <div class="musubi-page musubi-page--index">
    <header class="musubi-page__head">
      <h1>{{ title }}</h1>
      <p v-if="description" class="text-muted">{{ description }}</p>
    </header>

    <MusubiTabulator
      :rows="rows"
      :columns="columns"
      :storage-key="storageKey"
      :initial-sort="initialSort"
      :page-size="25"
      :loading="loading"
      :error="error"
    />
  </div>
</template>

<script>
/**
 * AdminEntityIndex — reusable list-page shell. Per-entity admin pages
 * (`AdminLists` / `AdminSubscribers` / etc.) compose this component with
 * their endpoint + column defs + title.
 *
 * V1 = read-only Tabulator. Per-row click-through to detail pages comes
 * with the entity detail surfaces (P2.4+ for templates, P3+ for the
 * heavier entities).
 */
import useApi from "@apexl/icarus-core/src/services/api";
import MusubiTabulator from "@root/components/MusubiTabulator.vue";

export default {
  name: 'AdminEntityIndex',
  components: { MusubiTabulator },
  props: {
    title:       { type: String,  required: true },
    description: { type: String,  default: '' },
    /** API endpoint path returning `{ data: [...] }`. */
    endpoint:    { type: String,  required: true },
    /** Tabulator column defs — see MusubiTabulator.vue for shape. */
    columns:     { type: Array,   required: true },
    /** localStorage key root for column / sort / filter persistence. */
    storageKey:  { type: String,  required: true },
    /** Default sort — `{ column, dir }`. */
    initialSort: { type: Object,  default: () => ({ column: 'created', dir: 'desc' }) },
  },
  data() {
    return {
      api: useApi(),
      rows: [],
      loading: false,
      error: null,
    };
  },
  mounted() {
    this.load();
  },
  watch: {
    endpoint() { this.load(); },
  },
  methods: {
    async load() {
      this.loading = true;
      this.error = null;
      this.rows = [];
      try {
        const res = await this.api.get(this.endpoint);
        const payload = res?.data?.data ?? res?.data ?? res;
        this.rows = Array.isArray(payload) ? payload : (payload?.rows || []);
      } catch (e) {
        this.error = e?.message || 'Failed to load.';
      } finally {
        this.loading = false;
      }
    },
  },
};
</script>
